import { formatRecordedDate } from '@/utils/recordedValues'
/**
 * Settings Page
 * User and tenant settings management.
 *
 * Profile, Notifications, and Organization tabs now PERSIST — TASK-04:
 *  - Profile      → users service `updateMe` (PUT /users/me).
 *  - Notifications→ users service `updatePreferences` (PUT /users/me).
 *  - Organization → tenant service `update` (PATCH /tenants/{id}).
 * In mock mode the changes survive in-session; in live mode they round-trip to
 * the backend.
 */

import { useState, useEffect } from 'react'
import {
  User,
  Bell,
  Lock,
  Building2,
  AlertCircle,
  ShieldCheck,
  Smartphone,
  Laptop,
  Globe,
  Trash2,
  Copy,
  Check,
  LogOut,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import type { AuthUser } from '@/types/auth.types'
import { getRoleDisplayName } from '@/types/auth.types'
import type { UserPreferences } from '@/types/user.types'
import { getUserService } from '@/services/users'
import { getTenantService } from '@/services/tenant'
import { useSessionStore } from '@/store/sessionStore'
import { toast } from '@/store/toastStore'
import { persistAuthUserPatch } from '@/utils/authUserPatch'

/** Read the preferences blob off the auth user (AuthUser type omits it, but the
 * backend may attach it at runtime). */
function readPreferences(user: AuthUser): UserPreferences {
  return (user as unknown as { preferences?: UserPreferences }).preferences ?? {}
}

export default function SettingsPage() {
  const { user, hasPermission } = useAuth()
  const [activeTab, setActiveTab] = useState<'profile' | 'account' | 'notifications' | 'tenant'>(
    'profile'
  )

  if (!user) return null

  const canManageTenant = hasPermission('txh.settings.update')

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'account', label: 'Account', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    ...(canManageTenant ? [{ id: 'tenant', label: 'Organization', icon: Building2 }] : []),
  ] as const

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-text-primary">Settings</h1>
        <p className="text-text-secondary mt-1">Manage your account and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <div className="card card-elevated p-2 space-y-1">
            {tabs.map(tab => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                      : 'text-text-secondary hover:bg-surface-overlay'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3">
          <div className="card card-elevated p-6">
            {activeTab === 'profile' && <ProfileTab user={user} />}
            {activeTab === 'account' && <AccountTab user={user} />}
            {activeTab === 'notifications' && <NotificationsTab user={user} />}
            {activeTab === 'tenant' && canManageTenant && <TenantTab user={user} />}
          </div>
        </div>
      </div>
    </div>
  )
}

// Profile Tab
function ProfileTab({ user }: { user: AuthUser }) {
  const initialFirst = user.firstName ?? user.fullName?.split(' ')[0] ?? ''
  const initialLast = user.lastName ?? user.fullName?.split(' ').slice(1).join(' ') ?? ''

  const [firstName, setFirstName] = useState(initialFirst)
  const [lastName, setLastName] = useState(initialLast)
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    if (!firstName.trim()) {
      setError('First name is required')
      return
    }
    setError(null)
    setIsSaving(true)
    const fullName = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ')
    try {
      await getUserService().updateMe({ fullName })
      persistAuthUserPatch({ firstName: firstName.trim(), lastName: lastName.trim(), fullName })
      toast.success('Profile updated', 'Your changes have been saved.')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again.'
      toast.error('Could not update profile', message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    setFirstName(initialFirst)
    setLastName(initialLast)
    setError(null)
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-text-primary mb-1">Profile Information</h2>
        <p className="text-sm text-text-secondary">
          Update your personal information and profile picture
        </p>
      </div>

      <div className="flex items-center gap-6">
        <div className="relative">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={`${firstName} ${lastName}`}
              className="w-24 h-24 rounded-full object-cover border-4 border-surface-overlay"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-primary-500 flex items-center justify-center text-white text-3xl font-bold border-4 border-surface-overlay">
              {(firstName.charAt(0) + lastName.charAt(0)).toUpperCase()}
            </div>
          )}
        </div>
        <div>
          <button type="button" className="btn btn-outline btn-sm" disabled>
            Change Photo
          </button>
          <p className="text-xs text-text-tertiary mt-2">JPG, PNG or GIF. Max 2MB.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            className={`input input-bordered w-full ${error ? 'border-red-500' : ''}`}
          />
          {error && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {error}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">Last Name</label>
          <input
            type="text"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            className="input input-bordered w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">Email</label>
          <input
            type="email"
            value={user.email}
            className="input input-bordered w-full opacity-60 cursor-not-allowed"
            disabled
          />
          <p className="text-xs text-text-tertiary mt-1">Email cannot be changed</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">Role</label>
          <div className="flex items-center h-11 px-4 rounded-lg bg-surface-overlay text-text-secondary">
            {getRoleDisplayName(user.role)}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-surface-overlay">
        <button type="button" className="btn btn-outline" onClick={handleReset} disabled={isSaving}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <span className="flex items-center gap-2">
              <span className="loading loading-spinner loading-sm" />
              Saving…
            </span>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </div>
  )
}

// Account Tab
function AccountTab({ user }: { user: AuthUser }) {
  const {
    sessions,
    loadUserSessions,
    terminateSession,
    terminateAllOtherSessions,
    isLoading: isSessionsLoading,
  } = useSessionStore()

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false)
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [copiedSecret, setCopiedSecret] = useState(false)
  const [terminatingId, setTerminatingId] = useState<string | null>(null)
  const [isTerminatingOthers, setIsTerminatingOthers] = useState(false)

  const totpSecret = 'JBSWY3DPEHPK3PXP'

  useEffect(() => {
    if (user.id) {
      loadUserSessions(user.id)
    }
  }, [user.id, loadUserSessions])

  const handleCopySecret = () => {
    navigator.clipboard.writeText(totpSecret)
    setCopiedSecret(true)
    setTimeout(() => setCopiedSecret(false), 2000)
  }

  const handleVerifyTwoFactor = () => {
    if (verificationCode.length !== 6) {
      toast.error('Invalid code', 'Please enter a 6-digit authentication code')
      return
    }
    setTwoFactorEnabled(true)
    setShowTwoFactorSetup(false)
    setVerificationCode('')
    toast.success('2FA Enabled', 'Two-factor authentication is now active on your account.')
  }

  const handleDisableTwoFactor = () => {
    if (window.confirm('Are you sure you want to disable two-factor authentication?')) {
      setTwoFactorEnabled(false)
      toast.info('2FA Disabled', 'Two-factor authentication has been disabled.')
    }
  }

  const handleRevokeSession = async (sessionId: string) => {
    setTerminatingId(sessionId)
    try {
      await terminateSession(sessionId)
      toast.success('Session revoked', 'The selected session has been terminated.')
    } catch {
      toast.error('Error', 'Failed to revoke session.')
    } finally {
      setTerminatingId(null)
    }
  }

  const handleRevokeAllOtherSessions = async () => {
    if (window.confirm('Are you sure you want to sign out of all other devices?')) {
      setIsTerminatingOthers(true)
      try {
        const count = await terminateAllOtherSessions(user.id, user.currentSessionId || '')
        toast.success('Sessions revoked', `Terminated ${count} other active session(s).`)
      } catch {
        toast.error('Error', 'Failed to revoke other sessions.')
      } finally {
        setIsTerminatingOthers(false)
      }
    }
  }

  const activeSessions = sessions.filter(s => s.status === 'active')

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-text-primary mb-1">Account Settings</h2>
        <p className="text-sm text-text-secondary">Manage your account security and preferences</p>
      </div>

      {/* Password Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-text-primary">Change Password</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Current Password
            </label>
            <input
              type="password"
              className="input input-bordered w-full"
              placeholder="Enter current password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">New Password</label>
            <input
              type="password"
              className="input input-bordered w-full"
              placeholder="Enter new password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Confirm New Password
            </label>
            <input
              type="password"
              className="input input-bordered w-full"
              placeholder="Confirm new password"
            />
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => toast.success('Password updated', 'Your password has been changed.')}
        >
          Update Password
        </button>
      </div>

      {/* Two-Factor Authentication (2FA) Section */}
      <div className="pt-6 border-t border-surface-overlay space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-text-primary">
                Two-Factor Authentication (2FA)
              </h3>
              <p className="text-sm text-text-secondary">
                Add an extra layer of security using an authenticator app (Google Authenticator,
                Authy).
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`badge ${
                twoFactorEnabled ? 'badge-success' : 'bg-surface-overlay text-text-secondary'
              }`}
            >
              {twoFactorEnabled ? 'Enabled' : 'Disabled'}
            </span>
            {twoFactorEnabled ? (
              <button
                type="button"
                onClick={handleDisableTwoFactor}
                className="btn btn-outline btn-sm text-red-600 border-red-600 hover:bg-red-50 dark:text-red-400 dark:border-red-400 dark:hover:bg-red-900/20"
              >
                Disable 2FA
              </button>
            ) : !showTwoFactorSetup ? (
              <button
                type="button"
                onClick={() => setShowTwoFactorSetup(true)}
                className="btn btn-primary btn-sm"
              >
                Set Up 2FA
              </button>
            ) : null}
          </div>
        </div>

        {/* 2FA Setup Flow */}
        {!twoFactorEnabled && showTwoFactorSetup && (
          <div className="card p-4 bg-surface-overlay/50 border border-surface-overlay rounded-lg space-y-4 animate-in slide-in-from-top-2 duration-200">
            <div className="space-y-1">
              <h4 className="font-semibold text-text-primary text-sm">
                Step 1: Add TaxHive to your authenticator app
              </h4>
              <p className="text-xs text-text-secondary">
                Scan or enter the setup key below into your authenticator app (Google Authenticator,
                1Password, Authy).
              </p>
            </div>

            <div className="flex items-center gap-2 max-w-sm">
              <code className="px-3 py-2 bg-surface rounded text-sm font-mono text-text-primary border border-surface-overlay flex-1">
                {totpSecret}
              </code>
              <button
                type="button"
                onClick={handleCopySecret}
                className="btn btn-outline btn-sm flex items-center gap-1"
              >
                {copiedSecret ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
                {copiedSecret ? 'Copied' : 'Copy'}
              </button>
            </div>

            <div className="space-y-1 pt-2">
              <h4 className="font-semibold text-text-primary text-sm">
                Step 2: Enter 6-digit confirmation code
              </h4>
              <p className="text-xs text-text-secondary">
                Enter the 6-digit code generated by your app to verify setup.
              </p>
              <div className="flex items-center gap-3 pt-2">
                <input
                  type="text"
                  maxLength={6}
                  value={verificationCode}
                  onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="input input-bordered w-36 text-center text-lg font-mono tracking-widest"
                />
                <button
                  type="button"
                  onClick={handleVerifyTwoFactor}
                  disabled={verificationCode.length !== 6}
                  className="btn btn-primary btn-sm"
                >
                  Verify & Activate
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowTwoFactorSetup(false)
                    setVerificationCode('')
                  }}
                  className="btn btn-ghost btn-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Active Sessions Section */}
      <div className="pt-6 border-t border-surface-overlay space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400">
              <Laptop className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-text-primary">Active Sessions</h3>
              <p className="text-sm text-text-secondary">
                Devices currently signed in to your account.
              </p>
            </div>
          </div>
          {activeSessions.length > 1 && (
            <button
              type="button"
              onClick={handleRevokeAllOtherSessions}
              disabled={isTerminatingOthers}
              className="btn btn-outline btn-sm text-red-600 border-red-600 hover:bg-red-50 dark:text-red-400 dark:border-red-400 dark:hover:bg-red-900/20 flex items-center gap-1"
            >
              <LogOut className="w-4 h-4" />
              {isTerminatingOthers ? 'Revoking…' : 'Sign Out All Other Sessions'}
            </button>
          )}
        </div>

        {isSessionsLoading ? (
          <div className="p-4 text-center text-sm text-text-secondary">
            Loading active sessions…
          </div>
        ) : activeSessions.length === 0 ? (
          <div className="p-4 text-center text-sm text-text-secondary border border-dashed border-surface-overlay rounded-lg">
            No other active sessions found.
          </div>
        ) : (
          <div className="space-y-3">
            {activeSessions.map(s => {
              const isCurrent =
                s.sessionId === user.currentSessionId ||
                s.isCurrentSession ||
                activeSessions.length === 1
              const DeviceIcon = s.deviceType === 'mobile' ? Smartphone : Laptop

              return (
                <div
                  key={s.sessionId}
                  className="p-4 rounded-lg border border-surface-overlay bg-surface-overlay/30 flex items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-surface border border-surface-overlay">
                      <DeviceIcon className="w-5 h-5 text-text-secondary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-text-primary text-sm">
                          {s.browser || 'Browser'} on {s.os || 'Operating System'}
                        </p>
                        {isCurrent && (
                          <span className="badge badge-success text-xs">Current Session</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-text-tertiary mt-0.5">
                        <span className="flex items-center gap-1">
                          <Globe className="w-3 h-3" />
                          {s.ipAddress || 'Unknown IP'}{' '}
                          {s.city ? `(${s.city}, ${s.country || ''})` : ''}
                        </span>
                        <span>•</span>
                        <span>Last active: {formatRecordedDate(s.lastActivityAt)}</span>
                      </div>
                    </div>
                  </div>

                  {!isCurrent && (
                    <button
                      type="button"
                      onClick={() => handleRevokeSession(s.sessionId)}
                      disabled={terminatingId === s.sessionId}
                      className="btn btn-ghost btn-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 flex items-center gap-1"
                    >
                      <Trash2 className="w-4 h-4" />
                      {terminatingId === s.sessionId ? 'Revoking…' : 'Revoke'}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Session Info */}
      <div className="pt-6 border-t border-surface-overlay">
        <h3 className="text-lg font-medium text-text-primary mb-4">Session Information</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-text-secondary">Last Login</span>
            <span className="text-text-primary font-medium">
              {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'N/A'}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-text-secondary">Account Created</span>
            <span className="text-text-primary font-medium">
              {formatRecordedDate(user.createdAt)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-text-secondary">Status</span>
            <span className={`badge ${user.status === 'active' ? 'badge-success' : 'badge-ghost'}`}>
              {user.status === 'unknown' ? 'Not recorded' : user.status}
            </span>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="pt-6 border-t border-surface-overlay">
        <h3 className="text-lg font-medium text-red-600 dark:text-red-400 mb-2">Danger Zone</h3>
        <p className="text-sm text-text-secondary mb-4">These actions are irreversible</p>
        <button className="btn btn-outline text-red-600 dark:text-red-400 border-red-600 dark:border-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
          Deactivate Account
        </button>
      </div>
    </div>
  )
}

// Notifications Tab
function NotificationsTab({ user }: { user: AuthUser }) {
  const stored = readPreferences(user)
  const storedNotifications = stored.notifications

  // Hydrate the channel toggles from the persisted preferences blob. The blob
  // stores coarse email/push/sms channels; the per-event toggles below default
  // to the matching channel value.
  const emailDefault = storedNotifications?.email ?? true
  const pushDefault = storedNotifications?.push ?? false
  const extra = (stored.dashboardLayout?.notificationDetail ?? {}) as Record<string, boolean>

  const [notifications, setNotifications] = useState({
    emailCaseUpdates: extra.emailCaseUpdates ?? emailDefault,
    emailHearingReminders: extra.emailHearingReminders ?? emailDefault,
    emailDocumentShared: extra.emailDocumentShared ?? emailDefault,
    pushCaseUpdates: extra.pushCaseUpdates ?? pushDefault,
    pushHearingReminders: extra.pushHearingReminders ?? pushDefault,
    pushDocumentShared: extra.pushDocumentShared ?? pushDefault,
    digestDaily: extra.digestDaily ?? false,
    digestWeekly: extra.digestWeekly ?? true,
  })
  const [isSaving, setIsSaving] = useState(false)

  const toggleNotification = (key: keyof typeof notifications) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    // Coarse channel flags derived from the per-event toggles, plus the full
    // detail map preserved under dashboardLayout so nothing is lost.
    const anyEmail =
      notifications.emailCaseUpdates ||
      notifications.emailHearingReminders ||
      notifications.emailDocumentShared
    const anyPush =
      notifications.pushCaseUpdates ||
      notifications.pushHearingReminders ||
      notifications.pushDocumentShared

    const nextPreferences: UserPreferences = {
      ...stored,
      notifications: {
        email: anyEmail,
        push: anyPush,
        sms: storedNotifications?.sms ?? false,
      },
      dashboardLayout: {
        ...(stored.dashboardLayout ?? {}),
        notificationDetail: notifications,
      },
    }

    try {
      await getUserService().updatePreferences(nextPreferences)
      // AuthUser's TS type omits `preferences`; persist it as an extra field.
      persistAuthUserPatch({ preferences: nextPreferences } as unknown as Partial<AuthUser>)
      toast.success('Preferences saved', 'Your notification settings were updated.')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again.'
      toast.error('Could not save preferences', message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    setNotifications({
      emailCaseUpdates: true,
      emailHearingReminders: true,
      emailDocumentShared: true,
      pushCaseUpdates: false,
      pushHearingReminders: true,
      pushDocumentShared: false,
      digestDaily: false,
      digestWeekly: true,
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-text-primary mb-1">Notification Preferences</h2>
        <p className="text-sm text-text-secondary">Choose how you want to be notified</p>
      </div>

      {/* Email Notifications */}
      <div>
        <h3 className="text-lg font-medium text-text-primary mb-4">Email Notifications</h3>
        <div className="space-y-3">
          <NotificationToggle
            label="Case Updates"
            description="Get notified when cases you're assigned to are updated"
            checked={notifications.emailCaseUpdates}
            onChange={() => toggleNotification('emailCaseUpdates')}
          />
          <NotificationToggle
            label="Hearing Reminders"
            description="Receive reminders for upcoming hearings"
            checked={notifications.emailHearingReminders}
            onChange={() => toggleNotification('emailHearingReminders')}
          />
          <NotificationToggle
            label="Document Shared"
            description="Get notified when documents are shared with you"
            checked={notifications.emailDocumentShared}
            onChange={() => toggleNotification('emailDocumentShared')}
          />
        </div>
      </div>

      {/* Push Notifications */}
      <div className="pt-6 border-t border-surface-overlay">
        <h3 className="text-lg font-medium text-text-primary mb-4">Push Notifications</h3>
        <div className="space-y-3">
          <NotificationToggle
            label="Case Updates"
            description="Browser notifications for case updates"
            checked={notifications.pushCaseUpdates}
            onChange={() => toggleNotification('pushCaseUpdates')}
          />
          <NotificationToggle
            label="Hearing Reminders"
            description="Browser notifications for hearing reminders"
            checked={notifications.pushHearingReminders}
            onChange={() => toggleNotification('pushHearingReminders')}
          />
          <NotificationToggle
            label="Document Shared"
            description="Browser notifications for shared documents"
            checked={notifications.pushDocumentShared}
            onChange={() => toggleNotification('pushDocumentShared')}
          />
        </div>
      </div>

      {/* Digest */}
      <div className="pt-6 border-t border-surface-overlay">
        <h3 className="text-lg font-medium text-text-primary mb-4">Email Digest</h3>
        <div className="space-y-3">
          <NotificationToggle
            label="Daily Digest"
            description="Receive a daily summary of all activities"
            checked={notifications.digestDaily}
            onChange={() => toggleNotification('digestDaily')}
          />
          <NotificationToggle
            label="Weekly Digest"
            description="Receive a weekly summary of all activities"
            checked={notifications.digestWeekly}
            onChange={() => toggleNotification('digestWeekly')}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-surface-overlay">
        <button type="button" className="btn btn-outline" onClick={handleReset} disabled={isSaving}>
          Reset to Default
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <span className="flex items-center gap-2">
              <span className="loading loading-spinner loading-sm" />
              Saving…
            </span>
          ) : (
            'Save Preferences'
          )}
        </button>
      </div>
    </div>
  )
}

// Tenant/Organization Tab
function TenantTab({ user }: { user: AuthUser }) {
  const { hasPermission } = useAuth()
  const canManageTenant = hasPermission('txh.settings.update')
  const [tenantName, setTenantName] = useState(user.tenantName ?? '')
  const [timezone, setTimezone] = useState('Asia/Kolkata')
  const [address, setAddress] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    if (!canManageTenant) return
    setIsSaving(true)
    try {
      await getTenantService().update(user.tenantId, {
        tenantName: tenantName.trim(),
        settings: { timezone },
        firmAddress: address.trim()
          ? {
              line1: address.trim(),
              city: '',
              state: '',
              pincode: '',
              country: 'India',
            }
          : undefined,
      })
      toast.success('Organization updated', 'Your changes have been saved.')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again.'
      toast.error('Could not update organization', message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-text-primary mb-1">Organization Settings</h2>
        <p className="text-sm text-text-secondary">Manage your firm's settings</p>
      </div>

      {/* Organization Info */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-text-primary">Organization Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Organization Name
            </label>
            <input
              type="text"
              value={tenantName}
              onChange={e => setTenantName(e.target.value)}
              className="input input-bordered w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Organization ID
            </label>
            <input
              type="text"
              value={user.tenantId}
              className="input input-bordered w-full opacity-60 cursor-not-allowed"
              disabled
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Time Zone</label>
            <select
              value={timezone}
              onChange={e => setTimezone(e.target.value)}
              className="input input-bordered w-full"
            >
              <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
              <option value="Asia/Dubai">Asia/Dubai (GST)</option>
              <option value="Europe/London">Europe/London (GMT/BST)</option>
              <option value="America/New_York">America/New_York (EST)</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-text-primary mb-2">Address</label>
            <textarea
              className="input input-bordered w-full h-24 resize-none"
              placeholder="Enter organization address"
              value={address}
              onChange={e => setAddress(e.target.value)}
            />
          </div>
        </div>
      </div>

      <p className="text-sm text-text-secondary">
        For full firm profile, branding, and subscription management, visit{' '}
        <span className="font-medium text-text-primary">Organization Settings</span>.
      </p>

      <div className="flex justify-end gap-3 pt-4 border-t border-surface-overlay">
        <button
          type="button"
          className="btn btn-outline"
          onClick={() => {
            setTenantName(user.tenantName ?? '')
            setTimezone('Asia/Kolkata')
            setAddress('')
          }}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <span className="flex items-center gap-2">
              <span className="loading loading-spinner loading-sm" />
              Saving…
            </span>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </div>
  )
}

// Notification Toggle Component
function NotificationToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string
  description: string
  checked: boolean
  onChange: () => void
}) {
  return (
    <div className="flex items-start justify-between py-3">
      <div className="flex-1">
        <p className="font-medium text-text-primary">{label}</p>
        <p className="text-sm text-text-secondary">{description}</p>
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input type="checkbox" checked={checked} onChange={onChange} className="sr-only peer" />
        <div className="w-11 h-6 bg-surface-overlay peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
      </label>
    </div>
  )
}
